// Port of Flutter's favDevicesScreen.dart — the first tab.
//
// AppBar reads "Welcome, <Name> <Surname>". The grid is the Dart's
// maxCrossAxisExtent 250 / aspect 0.9 / spacing 10 / padding 8, and each card
// shows the device picture, its auto-scrolling name and a 📍Location > Room
// line. Like the Dart, an empty favourites list bounces the user to the
// Locations tab once (so a fresh account doesn't land on an empty screen).
//
// Beyond Flutter: a "Hide offline devices" checkbox, remembered in
// localStorage — see core/hooks/useHideOffline for what counts as offline and
// why nothing is hidden until it is known to be.

import React, { useEffect, useRef } from 'react';
import {
  Box, Typography, useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { CenterTitleBar } from '../../components/MobileAppBar';
import DeviceCard from '../../components/DeviceCard';
import SensorCard from '../../components/SensorCard';
import ExtentGrid from '../../components/ExtentGrid';
import { useApp } from '../../../../core/state/AppContext';
import { useHideOffline, useOnlineOnly } from '../../../../core/hooks/useHideOffline';
import { isSensorDevice } from '../../../../core/devices/sensorState';
import { HOME_TAB_FAVOURITES } from '../../../../core/nav/appNav';

export default function FavouritesTab({ goTab }) {
  const theme = useTheme();
  const c = theme.sli;
  const navigate = useNavigate();
  const {
    user, favourites, favouritesLoading, refreshFavourites, toggleFavourite,
  } = useApp();
  const redirectedRef = useRef(false);
  const [hideOffline] = useHideOffline();
  // The redirect below still watches the FULL list — hiding every card must not
  // bounce the user off the tab they chose to filter.
  const { devices: shown, hiddenCount } = useOnlineOnly(favourites, hideOffline);

  useEffect(() => {
    if (favouritesLoading || redirectedRef.current) return;
    if (favourites.length === 0) {
      redirectedRef.current = true;
      goTab(1);
    }
  }, [favourites, favouritesLoading, goTab]);

  const who = user ? `${user.Name || ''} ${user.Surname || ''}`.trim() : 'User';

  // Back from the device returns HERE, not up through its room and location —
  // see `returnTo` in core/devices/deviceNav.
  const openDevice = (d) => navigate(`/device/${d.UniqueNumber}`, {
    state: { device: d, from: { returnTo: `/home?tab=${HOME_TAB_FAVOURITES}` } },
  });
  const onStar = async (d, isFav) => { await toggleFavourite(d, isFav); await refreshFavourites(); };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: c.back }}>
      <CenterTitleBar title={`Welcome, ${who || 'User'}`} />

      {/* No checkbox here — there is ONE, on the Locations tab, and it governs
          every list in the app (core/hooks/useHideOffline). What this screen
          owes the user is the COUNT: a filter must say where it is acting, even
          when it is switched on somewhere else. */}
      {hideOffline && hiddenCount > 0 && (
        <Typography sx={{ color: c.text, opacity: 0.65, fontSize: 12, px: 2, pt: 0.5 }}>
          {`${hiddenCount} offline ${hiddenCount > 1 ? 'devices are' : 'device is'} hidden`}
        </Typography>
      )}

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 1 }}>
        {favourites.length === 0 ? (
          <Typography sx={{ color: c.text, p: 2 }}>No Devices Found</Typography>
        ) : shown.length === 0 ? (
          <Typography sx={{ color: c.text, p: 2 }}>All devices are offline</Typography>
        ) : (
          <ExtentGrid maxExtent={250} childAspectRatio={0.9} gap={10}>
            {/* A starred sensor reads the same here as it does in its room:
                red or green, not a picture of a device you cannot press. The
                favourites reply carries no Outputs/Inputs, which is why
                isSensorDevice keys on the DeviceID first — see
                core/devices/sensorState. */}
            {shown.map((d) => (isSensorDevice(d) ? (
              <SensorCard
                key={d.UniqueNumber}
                device={d}
                showLocation
                onOpen={openDevice}
                onToggleFavourite={onStar}
              />
            ) : (
              <DeviceCard
                key={d.UniqueNumber}
                device={d}
                showLocation
                onOpen={openDevice}
                onToggleFavourite={onStar}
              />
            )))}
          </ExtentGrid>
        )}
      </Box>
    </Box>
  );
}
